'use client';

import { buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  DayPicker,
  getDefaultClassNames,
  type DayButtonProps,
  type DropdownProps
} from '@daypicker/react';

function CalendarDropdown(props: DropdownProps) {
  const { options, value, onChange } = props;

  const handleValueChange = (newValue: string) => {
    const event = {
      target: { value: newValue }
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange?.(event);
  };

  return (
    <Select
      value={value !== undefined ? String(value) : undefined}
      onValueChange={handleValueChange}
    >
      <SelectTrigger
        size='sm'
        className='h-7 gap-0.5 border-none bg-transparent px-1.5 font-medium shadow-none focus-visible:ring-0'
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options?.map(({ value: v, label, disabled }) => (
          <SelectItem key={v} value={String(v)} disabled={disabled}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout,
  buttonVariant = 'outline',
  components,
  locale,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: VariantProps<typeof buttonVariants>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();
  const isDropdown = captionLayout?.startsWith('dropdown');

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn(
        'bg-background p-3 [--cell-size:--spacing(8)]',
        'rtl:**:[.rdp-button_next>svg]:rotate-180',
        'rtl:**:[.rdp-button_previous>svg]:rotate-180',
        className
      )}
      captionLayout={captionLayout}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 opacity-50 select-none hover:opacity-100 aria-disabled:opacity-50 rtl:rotate-180',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'size-(--cell-size) p-0 opacity-50 select-none hover:opacity-100 aria-disabled:opacity-50 rtl:rotate-180',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'relative z-10 flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'has-focus:border-ring border-input shadow-xs relative inline-flex items-center rounded-md border',
          defaultClassNames.dropdown_root
        ),
        caption_label: cn(
          'select-none font-medium',
          isDropdown ? 'sr-only' : 'truncate text-sm',
          defaultClassNames.caption_label
        ),
        month_grid: cn('mx-auto mt-4', defaultClassNames.month_grid),
        weekdays: cn('flex flex-row gap-4', defaultClassNames.weekdays),
        weekday: cn(
          'text-muted-foreground w-8 text-sm font-normal',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full items-start', defaultClassNames.week),
        day: cn(
          'flex size-(--cell-size) flex-1 items-center justify-center p-0 text-sm',
          defaultClassNames.day
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-(--cell-size) rounded-md p-0 font-normal transition-none aria-selected:opacity-100',
          defaultClassNames.day_button
        ),
        range_start: cn(
          'bg-accent rounded-s-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
          defaultClassNames.range_start
        ),
        range_end: cn(
          'bg-accent rounded-e-md [&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
          defaultClassNames.range_end
        ),
        range_middle: cn(
          'bg-accent !text-foreground [&>button]:bg-transparent [&>button]:!text-foreground [&>button]:hover:bg-transparent [&>button]:hover:!text-foreground',
          defaultClassNames.range_middle
        ),
        selected: cn(
          '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
          defaultClassNames.selected
        ),
        today: cn(
          '[&>button]:bg-accent [&>button]:text-accent-foreground',
          defaultClassNames.today
        ),
        outside: cn(
          'text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
          defaultClassNames.outside
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled
        ),
        hidden: cn('invisible flex-1', defaultClassNames.hidden),
        ...classNames
      }}
      components={{
        ...(isDropdown ? { Dropdown: CalendarDropdown } : {}),
        DayButton: CalendarDayButton,
        ...components
      }}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code ?? 'default', { month: 'short' })
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

function CalendarDayButton(props: DayButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const { children, modifiers, className, day: _day, ...rest } = props;

  React.useEffect(() => {
    if (modifiers?.focused) {
      ref.current?.focus();
    }
  }, [modifiers?.focused]);

  return (
    <button
      ref={ref}
      className={className}
      data-selected={modifiers?.selected || undefined}
      data-disabled={modifiers?.disabled || undefined}
      data-today={modifiers?.today || undefined}
      data-outside={modifiers?.outside || undefined}
      data-range-start={modifiers?.range_start || undefined}
      data-range-end={modifiers?.range_end || undefined}
      data-range-middle={modifiers?.range_middle || undefined}
      {...rest}
    >
      {children}
    </button>
  );
}

export { Calendar, CalendarDayButton };
